import React from "react";
import { useRefresh } from "react-admin";
import { isInteger } from "utils/validations";
import { useOfferings } from "hooks/useOfferings";
import { dataProvider } from "data";
import { apiMethods } from "data/api.constants";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { logError } from "utils/error";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { baseApiClient } from "data/modules/api/api.client";
import {
  DEFAULT_FORM_DATA,
  FORM_FIELDS,
  FORM_VALIDATIONS,
  NULL_PRICE,
} from "features/OfferingVariations/pages/OfferingVariationsList/Components/AddVariation/AddVariation.constants";
import { useAddVariations } from "features/OfferingVariations/pages/OfferingVariationsList/Components/AddVariation/utils/hooks/useAddVariation";
import { schedule_types_ids } from "features/Listings/constants/Listings.constants";
import { useOfferingVariationListStore } from "features/OfferingVariations/pages/OfferingVariationsList/store/useOfferingVariationList.store";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { is_empty } from "features/Common/utils/common.utils";
import { getTableConfig } from "features/OfferingVariations/pages/OfferingVariationsList/utils/OfferingVariationsList.tableConfig";
import constants from "constants/constants";
import {
  CREATE_OFFERING_VARIATION,
  GET_MEMBERSHIP_OFFERING_VARIATIONS,
  GET_MEMBERSHIP_OFFERING_VARIATIONS_PATHNAME,
  MANAGE_OFFERING_VARIATION_BRANDED_COMMUNITY,
  UPDATE_OFFERING_VARIATION,
} from "features/OfferingVariations/OfferingVariations.apis";
import {
  CREATE_VARIATION_CTA_TEXT,
  IS_DUPLICATE_VARIANT_ERROR,
  MEMBERSHIP_DETAILS,
  VARIANT_TYPE_IDS,
  VARIATION_KYC_DISPLAY_STRINGS,
} from "features/OfferingVariations/OfferingVariations.constants";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { isRequestSuccessful } from "utils/Utils";
import { updateVariationKycOverrideApi } from "features/OfferingVariations/utils/updateVariationKycOverride";

export const useOfferingVariations = ({ offeringUuid }) => {
  const { notify } = useNotifications();
  const refetch = useRefresh();
  const { fetchOfferingDetails, updateOffering } = useOfferings();
  const {
    parsePayloadOnAddClick,
    parsePayloadOnEditClick,
    parsePayloadOnDeletedClick,
  } = useAddVariations();

  const isEnabled = useOfferingVariationListStore((state) => state.isEnabled); // offering variation switch ; if false click of everyaction will be disabled in the page
  const setIsEnabled = useOfferingVariationListStore(
    (state) => state.setIsEnabled
  );
  const offeringDetails = useOfferingVariationListStore(
    (state) => state.offeringDetails
  );
  const setOfferingDetails = useOfferingVariationListStore(
    (state) => state.setOfferingDetails
  );
  const processing = useOfferingVariationListStore((state) => state.processing);
  const setProcessing = useOfferingVariationListStore(
    (state) => state.setProcessing
  );
  const setShowDeleteModal = useOfferingVariationListStore(
    (state) => state.setShowDeleteModal
  );
  const setShowAddVariation = useOfferingVariationListStore(
    (state) => state.setShowAddVariation
  );
  const selectedVariation = useOfferingVariationListStore(
    (state) => state.selectedVariation
  );
  const setSelectedVariation = useOfferingVariationListStore(
    (state) => state.setSelectedVariation
  );
  const showInternationalPrice = useOfferingVariationListStore(
    (state) => state.showInternationalPrice
  );
  const setShowInternationalPrice = useOfferingVariationListStore(
    (state) => state.setShowInternationalPrice
  );
  const formData = useOfferingVariationListStore((state) => state.formData);
  const setFormData = useOfferingVariationListStore(
    (state) => state.setFormData
  );
  const errorObject = useOfferingVariationListStore(
    (state) => state.errorObject
  );
  const setErrorObject = useOfferingVariationListStore(
    (state) => state.setErrorObject
  );
  const setColumnConfig = useOfferingVariationListStore(
    (state) => state.setColumnConfig
  );
  const isMembership = useOfferingVariationListStore(
    (state) => state.isMembership
  );

  const { data: getVariationList, refetch: refetchGetVariationList } =
    useCustomQuery({
      queryKey: GET_MEMBERSHIP_OFFERING_VARIATIONS,
      url: GET_MEMBERSHIP_OFFERING_VARIATIONS_PATHNAME({
        offeringUuid,
      }),
      enabled: !!offeringUuid,
    });

  const getListingDetails = async ({ forceFetch } = {}) => {
    if (processing) return;
    setProcessing(true);
    const response = await fetchOfferingDetails({
      offeringUuid,
      forceFetch,
    });
    if (!is_empty(response)) {
      setOfferingDetails(response);
      setIsEnabled(response.is_offering_variants_enabled);
      setFormData({
        ...formData,
        [FORM_FIELDS.subscription_plan_choice.key]: response?.course_end_choice,
      });
    }
    setProcessing(false);
  };

  const footerProps = {
    primaryBtnProps: {
      disabled: !isEnabled,
    },
    primarybtn: CREATE_VARIATION_CTA_TEXT,
    primaryClick: () => setShowAddVariation(true),
  };

  const updateFormData = ({ key, value }) => {
    let tempErrorObject = { ...errorObject };
    setFormData({ ...formData, [key]: value });
    delete tempErrorObject[key];
    setErrorObject({ ...tempErrorObject });
  };

  const validateForm = (form = formData) => {
    let errMsg = "";
    let tempErrorObject = { ...errorObject };
    for (let key in form) {
      if (
        [
          FORM_FIELDS.international_price.key,
          FORM_FIELDS.domestic_price.key,
        ].includes(key) &&
        (offeringDetails?.type != schedule_types_ids.branded_community ||
          !isMembership)
      ) {
        if (!isInteger(form[key])) {
          errMsg = "Invalid from data!";
          tempErrorObject[key] = "Required";
        }
        continue;
      } else if (
        is_empty(form[key]) &&
        offeringDetails?.type != schedule_types_ids.branded_community
      ) {
        errMsg = "Invalid from data!";
        tempErrorObject[key] = "Required";
        continue;
      } else if (
        key === FORM_FIELDS.title.key &&
        form[key].length > FORM_VALIDATIONS[FORM_FIELDS.title.key].maxLength
      ) {
        errMsg = "Invalid from data!";
        tempErrorObject[key] = `Max ${
          FORM_VALIDATIONS[FORM_FIELDS.title.key].maxLength
        } characters`;
        continue;
      } else if (
        key === FORM_FIELDS.title.key &&
        form[key].length < FORM_VALIDATIONS[FORM_FIELDS.title.key].minLength
      ) {
        errMsg = "Invalid from data!";
        tempErrorObject[key] = `Min ${
          FORM_VALIDATIONS[FORM_FIELDS.title.key].minLength
        } characters`;
        continue;
      }
    }
    setErrorObject(tempErrorObject);
    return errMsg;
  };

  const handleAddVariation = async () => {
    try {
      if (processing) return;
      setProcessing(true);
      if (offeringDetails?.type === schedule_types_ids.branded_community) {
        const errorsCheck = validateForm();
        if (!is_empty(errorsCheck)) {
          throw new Error(errorsCheck);
        }
        let payload = parsePayloadOnAddClick();
        if (payload) {
          const response = await baseApiClient.post(
            MANAGE_OFFERING_VARIATION_BRANDED_COMMUNITY,
            { ...payload }
          );
          if (isRequestSuccessful(response.status)) {
            resetStates();
            notify(
              response?.message || "Added variation succesfully",
              notification_color_keys.success
            );
          }
        }
      } else {
        let payload = { ...formData, listing_uuid: offeringDetails?.uuid };
        if (!showInternationalPrice) {
          delete payload[FORM_FIELDS.international_price.key];
        }
        const errorsCheck = validateForm();
        if (!is_empty(errorsCheck)) {
          throw new Error(errorsCheck);
        }
        const response = await dataProvider.custom_request(
          CREATE_OFFERING_VARIATION,
          apiMethods.POST,
          payload
        );
        if (isRequestSuccessful(response.status)) {
          resetStates();
          notify(response?.message, notification_color_keys.success);
        }
      }
    } catch (error) {
      logError({
        error,
        occuredAt:
          "src/features/OfferingVariations/pages/OfferingVariationsList/Components/AddVariation/useAddVariation.js",
        when: "on handleAddVariation",
      });

      if (
        error?.response?.data?.message &&
        error?.response?.data?.message.includes(IS_DUPLICATE_VARIANT_ERROR)
      ) {
        notify(IS_DUPLICATE_VARIANT_ERROR, notification_color_keys.error);
      } else {
        notify(
          error?.response?.data?.message ??
            error?.message ??
            SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    } finally {
      setProcessing(false);
    }
  };

  const updateVariation = async ({
    offering_variation_uuid = selectedVariation.uuid,
    updated_values = formData,
    is_delete = false,
  }) => {
    try {
      if (processing) return;
      setProcessing(true);
      if (offeringDetails?.type === schedule_types_ids.branded_community) {
        if (!is_delete) {
          const errorsCheck = validateForm();
          if (!is_empty(errorsCheck)) {
            throw new Error(errorsCheck);
          }
        }
        let payload = is_delete
          ? parsePayloadOnDeletedClick()
          : parsePayloadOnEditClick();
        if (payload) {
          const response = await baseApiClient.post(
            MANAGE_OFFERING_VARIATION_BRANDED_COMMUNITY,
            { ...payload }
          );
          if (isRequestSuccessful(response.status)) {
            resetStates();
            notify(
              response?.message || "Updated variation succesfully",
              notification_color_keys.success
            );
          }
        }
      } else {
        const payload = { uuid: offering_variation_uuid, updated_values };
        if (updated_values.is_active !== false) {
          // validate only if variation is updated not deleted
          const errorsCheck = validateForm();
          if (!is_empty(errorsCheck)) {
            throw new Error(errorsCheck);
          }
        }

        const response = await dataProvider.custom_request(
          UPDATE_OFFERING_VARIATION,
          apiMethods.POST,
          payload
        );
        if (isRequestSuccessful(response.status)) {
          resetStates();
          notify(response?.message, notification_color_keys.success);
          if (selectedVariation?.variant_type === VARIANT_TYPE_IDS.default) {
            await getListingDetails({ forceFetch: true });
          }
        }
      }
    } catch (error) {
      logError({
        error,
        occuredAt:
          "src/features/OfferingVariations/pages/OfferingVariationsList/Components/AddVariation/useAddVariation.js",
        when: "on handleAddVariation",
      });

      notify(
        error?.response?.data?.message ??
          error?.message ??
          SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
    } finally {
      setProcessing(false);
    }
  };

  const resetStates = (dontRefetch = false) => {
    setShowAddVariation(false);
    setShowDeleteModal(false);
    setShowInternationalPrice(false);
    setSelectedVariation(null);
    setFormData({
      ...DEFAULT_FORM_DATA,
      [FORM_FIELDS.subscription_plan_choice.key]:
        offeringDetails?.course_end_choice,
    });
    setErrorObject({});
    if (!dontRefetch) {
      refetch();
    }
  };

  const updateVariationKycOverride = async ({ record, override }) => {
    if (!record?.uuid) {
      notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
      return Promise.reject();
    }
    if (processing) return Promise.reject();
    setProcessing(true);
    try {
      const response = await updateVariationKycOverrideApi({
        offeringUuid,
        offeringType: offeringDetails?.type,
        record,
        override,
      });
      notify(
        response?.message ?? VARIATION_KYC_DISPLAY_STRINGS.update_success,
        notification_color_keys.success
      );
    } catch (error) {
      logError({
        error,
        occuredAt:
          "src/features/OfferingVariations/utils/hooks/useOfferingVariations.js",
        when: "on updateVariationKycOverride",
      });
      notify(
        error?.response?.data?.message ??
          error?.message ??
          SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
      throw error;
    } finally {
      setProcessing(false);
    }
  };

  const deleteVariation = () => {
    if (is_empty(selectedVariation)) {
      return notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
    }
    if (offeringDetails?.type === schedule_types_ids.branded_community) {
      updateVariation({
        is_delete: true,
      });
    } else {
      updateVariation({
        updated_values: { is_active: false },
      });
    }
  };

  const handleEditVariation = (record) => {
    if (!isEnabled) return;
    setSelectedVariation(record);
    let tempFormData = { ...formData };
    for (let key in record) {
      if (key in tempFormData) {
        tempFormData[key] = record[key];
      }
    }
    if (!is_empty(record[FORM_FIELDS.international_price.key])) {
      tempFormData[FORM_FIELDS.international_price.key] =
        record[FORM_FIELDS.international_price.key];
      setShowInternationalPrice(true);
    }
    setFormData(tempFormData);
    setShowAddVariation(true);
  };

  const handleToggleInternationalSwitch = (checked) => {
    const tempFormData = { ...formData };
    if (checked) {
      tempFormData[FORM_FIELDS.international_price.key] = "";
    } else {
      tempFormData[FORM_FIELDS.international_price.key] = NULL_PRICE;
      if (errorObject[FORM_FIELDS.international_price.key]) {
        setErrorObject((tempErrorObject) => {
          delete tempErrorObject[FORM_FIELDS.international_price.key];
          return tempErrorObject;
        });
      }
    }
    setShowInternationalPrice(checked);
    setFormData(tempFormData);
  };

  const closeModal = () => resetStates(true);

  const onEnableVariationSwitchChange = (checked) => {
    if (processing) return;
    setProcessing(true);
    updateOffering({
      offeringUuid: offeringDetails.uuid,
      updatedValues: {
        is_offering_variants_enabled: checked,
      },
      onEnd: () => setProcessing(false),
      onSuccess: (response) => {
        if (checked) {
          refetch();
        }
        setIsEnabled(checked);
        notify(response?.message, notification_color_keys.success);
      },
      onError: (error) => {
        logError({
          error,
          occuredAt:
            "src/features/OfferingVariations/utils/hooks/useOfferingVariations.js",
          when: "onEnableVariationSwitchChange",
        });
        notify(
          error?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      },
    });
  };

  React.useEffect(() => {
    if (offeringUuid) {
      refetchGetVariationList();
    }
  }, [offeringUuid]);

  React.useEffect(() => {
    if (
      getVariationList &&
      !is_empty(getVariationList) &&
      offeringDetails.type === constants.scheduleTypesId.branded_community &&
      isMembership
    ) {
      setProcessing(true);
      updateColumnConfig({ getVariationList });
    }
  }, [getVariationList]);

  const updateColumnConfig = ({ getVariationList }) => {
    const tableColumns = [...getTableConfig()];
    const membershipColumns = [];
    if (
      offeringDetails.type === constants.scheduleTypesId.branded_community &&
      getVariationList &&
      !is_empty(getVariationList) &&
      getVariationList?.offering_variants &&
      !checkIsArrayEmpty(getVariationList?.offering_variants)
    ) {
      let membershipDetailCheck = Object.keys(
        getVariationList?.offering_variants[0]
      ).includes(MEMBERSHIP_DETAILS);
      if (membershipDetailCheck) {
        membershipColumns.push({
          field: FORM_FIELDS.membership.key,
          headerName: FORM_FIELDS.membership.label,
          renderCell: (row) => row?.membership_details?.title,
        });
      }
      tableColumns.splice(1, 0, ...membershipColumns);
    }

    setColumnConfig([...tableColumns]);
    setProcessing(false);
  };

  const provider = {
    deleteVariation,
    handleToggleInternationalSwitch,
    updateFormData,
    handleAddVariation,
    handleEditVariation,
    updateVariation,
    updateVariationKycOverride,
    closeModal,
    setShowDeleteModal,
    onEnableVariationSwitchChange,
    footerProps,
    getListingDetails,
  };

  return {
    provider,
  };
};
